import React, { useState, useRef, useContext } from 'react'
import { func, bool, number } from 'prop-types'

import { filterType, categoriesType, trackingRoomInfoType } from 'models/propTypes'
import classNames from 'classnames'
import { useTranslation, Trans } from 'react-i18next'
import Icon from 'components/uiElements/Icon'
import SheetModal from 'components/uiElements/SheetModal'
import BouncingBallLoader from 'components/uiElements/BouncingBallLoader'
import Tooltip from 'components/Tooltip'
import useOutsideAlerter from 'hooks/useOutsideAlerter'
import { DEBUG_CATEGORY } from 'models/furniture/constants'
import { click } from 'utils/tracker'
import { FILTER_SOURCE_MENU, FILTER_SOURCE_BREADCRUMB } from 'models/tracking/constants'
import Search from '@ingka/search'
import SSRIcon from '@ingka/ssr-icon'
import magnifyingGlass from '@ingka/ssr-icon/paths/magnifying-glass'
import cross from '@ingka/ssr-icon/paths/cross'
import SugarcubeContext from 'apps/SugarcubeContext'
import CategoryFiltersContainer from 'containers/designSidepanel/ProductFilter/CategoryFiltersContainer'
import useSearchCompletions from './useSearchCompletions'

require('./style.scss')

const ProductFilter = ({
    furnitureFilter,
    updateFurnitureFilter,
    categories,
    clearFurnitureFilter,
    isFilterActive,
    numberProductItems,
    furnitureIsLoading,
    isCategoriesLoading,
    filterFeaturedFurniture,
    showFilterModal,
    setShowFilterModal,
    hasDebugCategory,
    roomInfo,
    hasFeaturedProducts,
}) => {
    const { t } = useTranslation()
    const [isEditing, setIsEditing] = useState(false)
    const searchAndDropdownRef = useRef(null)
    const { isKiosk } = useContext(SugarcubeContext)
    const [isSearchCollapsed, setIsSearchCollapsed] = useState(isKiosk)
    const clickOutsideInput = () => setIsEditing(false)
    useOutsideAlerter(searchAndDropdownRef, clickOutsideInput)

    const searchQuery = furnitureFilter.query || ''
    const [hoveredCategoryKey, setHoveredCategoryKey] = useState(null)
    const [query, setQuery] = useState(searchQuery)
    // activeCompletionIndex is used to manage the active selection when the user is using the up/down keys
    const [activeCompletionIndex, setActiveCompletionIndex] = useState(-1)
    const hasSelectedCategory = furnitureFilter.category && furnitureFilter.category !== '*'
    const hasSelectedSubCategory = furnitureFilter.subCategory && furnitureFilter.subCategory !== '*'
    const FEATURED_CATEGORY = 'FEATURED_CATEGORY'
    // We want to get search autocompletions when the user is not using the up and down keys.
    // This behavior is similar to that seen in the search auto completions on ikea.com
    const shouldAutocomplete = activeCompletionIndex === -1
    const { searchCompletions, clearSearchResults } = useSearchCompletions(query, shouldAutocomplete)

    // For the category list
    const selectedCategory = hoveredCategoryKey ? categories.find(({ key }) => key === hoveredCategoryKey) : null

    const onCategoryHover = (e) => {
        const categoryKey = e.target.getAttribute('data-category')
        setHoveredCategoryKey(categoryKey)
    }

    const onCategoryClick = (categoryKey, categoryLabel, filterSource) => {
        if (categoryKey === FEATURED_CATEGORY) {
            filterFeaturedFurniture()
        } else {
            const trackingInfo = {
                filterSource,
                categoryLabel,
            }
            updateFurnitureFilter({ category: categoryKey }, trackingInfo)
            setQuery('')
        }
        setShowFilterModal(false)
    }

    const onSubCategoryClick = (subCategory, subCategoryLabel) => {
        const trackingInfo = {
            categoryLabel: selectedCategory.label,
            subCategoryLabel,
            filterSource: FILTER_SOURCE_MENU,
        }
        updateFurnitureFilter(
            {
                category: hoveredCategoryKey,
                subCategory,
            },
            trackingInfo,
        )
        setQuery('')
        setShowFilterModal(false)
    }

    const clearSearchQuery = () => {
        setQuery('')
        setActiveCompletionIndex(-1)
        clearFurnitureFilter()
        clearSearchResults()
    }

    const handleBrowseByCategoryClick = () => {
        clearSearchResults()
        setShowFilterModal(true)
        click('DecorateRoom.Catalog.Browse', roomInfo)
    }

    const onBrowseClose = () => {
        setShowFilterModal(false)
        click('DecorateRoom.Catalog.Browse.Quit', roomInfo)
    }

    const onSearchClear = () => {
        clearSearchQuery()
        click('DecorateRoom.Catalog.Search.Clear', roomInfo)
    }

    const onSelectCompletion = (completion) => {
        setQuery(completion)
        setIsEditing(false)
        clearSearchResults()
        updateFurnitureFilter({ query: completion })
    }

    const onQueryKeyDown = (e) => {
        // Arrow key up
        if (e.keyCode === 38) {
            // if there an active selection for the search dropdown

            if (activeCompletionIndex !== -1) {
                const nextActiveIndex = activeCompletionIndex - 1
                setQuery(searchCompletions[nextActiveIndex])
                setActiveCompletionIndex(nextActiveIndex)
            }
        }

        // Arrow key down
        if (e.keyCode === 40) {
            // if the active selection is not at the last entry in the search dropdown
            if (activeCompletionIndex !== searchCompletions.length - 1) {
                const nextActiveIndex = activeCompletionIndex + 1
                setQuery(searchCompletions[nextActiveIndex])
                setActiveCompletionIndex(nextActiveIndex)
            }
        }
    }

    const handleQueryChange = (e) => {
        const {
            target: { value },
        } = e
        setQuery(value)
        setActiveCompletionIndex(-1)
        setIsEditing(true)
        if (value === '') {
            clearSearchResults()
        }

        if (e.keyCode === 13) {
            onSelectCompletion(value)
        }
    }

    const browseByCategoryContent = isKiosk ? null : (
        <button
            onClick={handleBrowseByCategoryClick}
            className="filterButton"
            data-testid="filter-category-btn"
            data-tip={t('design.browse by category')}
            data-for="product-browse"
        >
            <Icon code="menu" svg />
        </button>
    )

    // For the filter subHeader
    let filterCategory = null
    if (hasSelectedCategory) {
        if (furnitureFilter.category === DEBUG_CATEGORY) {
            filterCategory = { label: t('common.debug') }
        } else {
            filterCategory = categories.find(({ key }) => key === furnitureFilter.category)
        }
    }
    const filterSubCategory =
        hasSelectedSubCategory && filterCategory
            ? filterCategory.subCategories.find(({ key }) => key === furnitureFilter.subCategory)
            : null

    const numberItemsText = t('design.itemWithCount', { count: numberProductItems })
    const isSearchActive = !!furnitureFilter.query
    const isFeaturedFilterActive = furnitureFilter.subCatalog !== '*' || furnitureFilter.roomType !== '*'
    const showFilterSubheader = !furnitureIsLoading && ((isFilterActive && !isFeaturedFilterActive) || isSearchActive)
    const showFeaturedSubheader = hasFeaturedProducts && !showFilterSubheader && !furnitureIsLoading
    const placeHolderText = t('design.what are you looking for?')
    const tryAgainText = t('design.try again')
    const showSearchAutocompletions = !!searchCompletions.length && isEditing

    return (
        <div className="ProductFilter">
            <div className={classNames('filterContent', { isKiosk, isSearchCollapsed })}>
                <div className="filterHeader">
                    <Tooltip id="product-browse" place="right" />
                    {browseByCategoryContent}
                    {isKiosk && isSearchCollapsed && (
                        <button className="expandSearch" onClick={() => setIsSearchCollapsed(false)}>
                            <span>
                                <SSRIcon paths={magnifyingGlass} />
                            </span>
                        </button>
                    )}
                    <div
                        ref={searchAndDropdownRef}
                        className={classNames('searchInputWrapper', { isEditing: showSearchAutocompletions })}
                    >
                        <div onKeyDown={(e) => e.stopPropagation()}>
                            <Search
                                onChange={handleQueryChange}
                                onSearch={() => onSelectCompletion(query)}
                                value={query}
                                placeholder={placeHolderText}
                                onClear={onSearchClear}
                                data-testid="product-search-input"
                                onKeyDown={onQueryKeyDown}
                                actions={
                                    isKiosk && [
                                        {
                                            disabled: false,
                                            onClick: () => setIsSearchCollapsed(true),
                                            ssrIcon: cross,
                                        },
                                    ]
                                }
                            />
                        </div>

                        {showSearchAutocompletions && (
                            <div className="searchDropdown">
                                <ul className="searchResults">
                                    {searchCompletions.map((completion, index) => (
                                        <li
                                            key={`search-completion-${completion}`}
                                            className={classNames('searchResultItem', {
                                                isActive: index === activeCompletionIndex,
                                            })}
                                        >
                                            <button onClick={() => onSelectCompletion(completion)}>
                                                <span className="searchIcon">
                                                    <SSRIcon paths={magnifyingGlass} />
                                                </span>
                                                <span>{completion}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
                {isKiosk && <CategoryFiltersContainer />}
                {showFilterSubheader && (
                    <div className={classNames('filterSubheader', { isBrowseResult: !isSearchActive })}>
                        {isSearchActive ? (
                            <div className="selectedResults">
                                <div className="selectedResultsText">
                                    {numberProductItems ? (
                                        <Trans i18nKey="design.results from" searchQuery={searchQuery}>
                                            Results from <strong>&quot;{{ searchQuery }}&quot;</strong>
                                        </Trans>
                                    ) : (
                                        <Trans i18nKey="design.no matches" searchQuery={searchQuery}>
                                            We&apos;ve turned up no matches for{' '}
                                            <strong>&quot;{{ searchQuery }}&quot;</strong>
                                        </Trans>
                                    )}
                                </div>
                                {!numberProductItems && <div className="tryAgain">{tryAgainText}</div>}
                            </div>
                        ) : (
                            <div className="selectedCategories">
                                <span onClick={clearFurnitureFilter} className="headerSearch">
                                    {t('design.all Items')}
                                </span>
                                {filterCategory && (
                                    <span
                                        className={classNames('headerCategoryName', {
                                            isNotClickable: !hasSelectedSubCategory,
                                        })}
                                        onClick={() =>
                                            onCategoryClick(
                                                furnitureFilter.category,
                                                filterCategory.label,
                                                FILTER_SOURCE_BREADCRUMB,
                                            )
                                        }
                                    >
                                        <Icon svg code="chevron_right" />
                                        <div className="ellipsis">{filterCategory.label}</div>
                                    </span>
                                )}
                                {filterSubCategory && (
                                    <span className="headerSubCategoryName">
                                        <Icon svg code="chevron_right" />
                                        <div className="ellipsis">{filterSubCategory.label}</div>
                                    </span>
                                )}
                            </div>
                        )}
                        <div className={classNames('numberItems', { isSearchActive })}>{numberItemsText}</div>
                    </div>
                )}
                {showFeaturedSubheader && (
                    <div className={classNames('filterSubheader', { isBrowseResult: !isSearchActive })}>
                        <div className="selectedSubCatalog">
                            {isFeaturedFilterActive ? t('design.featured products') : t('design.all products')}
                        </div>
                        <div className="browseOther">
                            <button onClick={isFeaturedFilterActive ? clearFurnitureFilter : filterFeaturedFurniture}>
                                {isFeaturedFilterActive
                                    ? t('design.show more products')
                                    : t('design.show featured products')}
                            </button>
                        </div>
                    </div>
                )}
                {showFilterModal && (
                    <SheetModal onClose={onBrowseClose}>
                        <div className="cascadingLists" data-testid="filter-category-modal">
                            <div className="listWrapper">
                                <div className="listHeader">{t('design.products')}</div>
                                {isCategoriesLoading && <BouncingBallLoader />}
                                {categories.length && (
                                    <div className="categoryListContainer">
                                        <ul className="categoryList">
                                            {hasFeaturedProducts && (
                                                <li
                                                    data-category={FEATURED_CATEGORY}
                                                    className="categoryListItem"
                                                    onMouseEnter={onCategoryHover}
                                                    onClick={() => onCategoryClick(FEATURED_CATEGORY)}
                                                    role="presentation"
                                                >
                                                    {t('design.featured products')}
                                                </li>
                                            )}
                                            {hasDebugCategory && (
                                                <li
                                                    data-category={DEBUG_CATEGORY}
                                                    className="categoryListItem debug"
                                                    onMouseEnter={onCategoryHover}
                                                    onClick={() => onCategoryClick(DEBUG_CATEGORY)}
                                                    role="presentation"
                                                >
                                                    {t('common.debug')}
                                                </li>
                                            )}
                                            {categories.map(({ key, label }) => {
                                                const isActive = key === hoveredCategoryKey
                                                return (
                                                    <li
                                                        key={key}
                                                        data-category={key}
                                                        className={classNames('categoryListItem', { isActive })}
                                                        onMouseEnter={onCategoryHover}
                                                        onClick={() => onCategoryClick(key, label, FILTER_SOURCE_MENU)}
                                                        data-testid="product-filter-category"
                                                        role="presentation"
                                                    >
                                                        {label}
                                                        {isActive && <Icon code="chevron_right" svg />}
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            {selectedCategory && (
                                <div className="listWrapper">
                                    <div className="listHeader">{selectedCategory.label}</div>
                                    <div className="subCategoryListContainer">
                                        <ul className="subCategoryList">
                                            {selectedCategory.subCategories.map(({ key, label }) => (
                                                <li
                                                    key={key}
                                                    data-category={key}
                                                    className="categoryListItem"
                                                    onClick={() => onSubCategoryClick(key, label)}
                                                    data-testid="product-filter-sub-category"
                                                    role="presentation"
                                                >
                                                    {label}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    </SheetModal>
                )}
            </div>
        </div>
    )
}

ProductFilter.defaultProps = {
    roomInfo: null,
}

ProductFilter.propTypes = {
    furnitureFilter: filterType.isRequired,
    updateFurnitureFilter: func.isRequired,
    filterFeaturedFurniture: func.isRequired,
    categories: categoriesType.isRequired,
    clearFurnitureFilter: func.isRequired,
    isFilterActive: bool.isRequired,
    numberProductItems: number.isRequired,
    furnitureIsLoading: bool.isRequired,
    isCategoriesLoading: bool.isRequired,
    showFilterModal: bool.isRequired,
    setShowFilterModal: func.isRequired,
    hasDebugCategory: bool.isRequired,
    roomInfo: trackingRoomInfoType,
    hasFeaturedProducts: bool.isRequired,
}

export default ProductFilter
